import { ReactNode } from 'react';
import { EOAuthConnectionStatus, TOAuthProvider } from './calendarIntegrations';
import { TConvertKeys, TNullable, ELanguage } from './common';
import { IPeriod } from './date';
import { IReportFilterParams } from './reports';
import { IUploadDocumentData } from './documents';

export interface IPhone {
  country?: string;
  number?: string;
  extension?: string;
}

export enum EUserRoles {
  ADMIN = 'admin',
  COORDINATOR = 'coordinator',
  PROVIDER = 'provider',
  PATIENT = 'patient',
  GUEST = 'guest',
  DEVICE = 'device',
}

export interface ILocation {
  subdivision?: string;
  country: string;
  state?: string;
  city?: string;
  zip?: string;
  address1?: string;
  address2?: string;
}

export type TLicenseLocation = Pick<ILocation, 'country' | 'state'>;

export type TTimeUnit = 'minutes' | 'hours' | 'days';

export type TNotificationsAlert = {
  value: number;
  unit: TTimeUnit;
};

export type TEditingNotification = TNullable<
  TNotificationsAlert & { index: number }
>;

export type TNotificationsAlertCamel = TConvertKeys<TNotificationsAlert>;

export interface IProfileNotifications {
  alerts: TNotificationsAlert[];
  enable_in_app: boolean;
  enable_email: boolean;
  enable_sms: boolean;
}

export type TProfileNotificationsCamel = TConvertKeys<IProfileNotifications>;

export interface IUserAvailability {
  is_online: boolean;
  is_in_meeting: boolean;
  in_waiting_room: boolean;
  is_available?: boolean;
}

export type TUserAvailabilityCamel = TConvertKeys<IUserAvailability>;

export interface IUserMeta {
  appointment_rating_dismissed_at: TNullable<string>;
  oauth_connections: IUserMetaOAuthConnection[];
  eula_accepted_at: string;
  tax_id?: TNullable<string>;
  insurance?: {
    name?: TNullable<string>;
    policy_number?: TNullable<string>;
  };
  is_available: boolean;
  reports: {
    filters: { [key: string]: IReportFilterParams };
  };
  npi?: string;
  profile_picture?: string;
  fax?: TNullable<IPhone>;
  height?: TNullable<number>;
  weight?: TNullable<number>;
  insurance_card_photo?: IUploadDocumentData;
  photo_id?: IUploadDocumentData;
  is_insurance_stale?: boolean;
}

interface IUserMetaOAuthConnection {
  id: string;
  provider: TOAuthProvider;
  provider_email: string;
  provider_display_name: string;
  expires_at: number;
  status: EOAuthConnectionStatus;
  created_at: string;
  last_token_refresh_at: string;
  last_sync_at: string;
}

export type TUserMetaOAuthConnectionCamel =
  TConvertKeys<IUserMetaOAuthConnection>;

export type TUserMetaCamel = TConvertKeys<IUserMeta>;

export interface IUserData {
  id: string;
  email: string;
  phone: TNullable<IPhone>;
  first_name: string;
  last_name: string;
  gender: string;
  dob: string;
  timezone: string;
  is_super_admin: boolean;
  is_active: boolean;
  is_autotimezone: boolean;
  license_locations: TLicenseLocation[];
  language: ELanguage;
  user_location: ILocation;
  medical_specialties: string[];
  languages_spoken: ELanguage[];
  user_institution_roles: TUserRoleInstitutionSettings[];
  notifications: IProfileNotifications;
  created_at: string;
  updated_at: string;
  calendar_settings: ICalendarSettings;
  availability: IUserAvailability;
  meta?: Partial<IUserMeta>;
}

export type TUserDataCamel = TConvertKeys<IUserData>;

export type TUserSummary = Pick<
  IUserData,
  | 'first_name'
  | 'last_name'
  | 'medical_specialties'
  | 'dob'
  | 'gender'
  | 'availability'
  | 'timezone'
> & {
  user_id: string;
  user_institution_role_id: string;
  role: EUserRoles;
  profile_picture?: string;
};

export type TUserSummaryCamel = TConvertKeys<TUserSummary>;

export type TUserWithUserInstitutionRole = TUserDataCamel & {
  userInstitutionRole: TUserRoleInstitutionSettingsCamel;
};

export interface ICalendarSettings {
  weekly_availability: IPeriod[];
}

export type TCalendarSettingsCamel = TConvertKeys<ICalendarSettings>;

// user.user_institution_roles TYPES

interface IUserRoleInstitutionSettingsBase<R extends EUserRoles> {
  id: string;
  user_id: string;
  institution_id: string;
  notifications: IProfileNotifications;
  role: R;
}

type TUserInstitutionRoleMetaDosespot = {
  is_active: boolean;
  clinic_id: number;
  user_id: number;
};

type IUserRoleInstitutionSettingsWithMeta<
  M,
  Required extends boolean = false,
> = Required extends true ? { meta: M } : { meta?: M };

type TUserRoleInstitutionSettingsDevice =
  IUserRoleInstitutionSettingsBase<EUserRoles.DEVICE> &
    IUserRoleInstitutionSettingsWithMeta<{ device_id: string }, true>;

type TUserRoleInstitutionSettingsProvider =
  IUserRoleInstitutionSettingsBase<EUserRoles.PROVIDER> &
    IUserRoleInstitutionSettingsWithMeta<{
      dosespot?: TUserInstitutionRoleMetaDosespot;
    }>;

type TUserRoleInstitutionSettingsDistributed<R extends EUserRoles> =
  R extends unknown
    ? IUserRoleInstitutionSettingsBase<R> &
        IUserRoleInstitutionSettingsWithMeta<unknown>
    : never;

type TUserRoleInstitutionSettingsOther =
  TUserRoleInstitutionSettingsDistributed<
    Exclude<EUserRoles, EUserRoles.DEVICE | EUserRoles.PROVIDER>
  >;

export type TUserRoleInstitutionSettings =
  | TUserRoleInstitutionSettingsDevice
  | TUserRoleInstitutionSettingsProvider
  | TUserRoleInstitutionSettingsOther;

export type TUserRoleInstitutionSettingsShort = Pick<
  TUserRoleInstitutionSettings,
  'id' | 'role'
>;

export type TUserRoleInstitutionSettingsCamel =
  TConvertKeys<TUserRoleInstitutionSettings>;

export type TUserRoleInstitutionSettingsCamelByRole<R extends EUserRoles> =
  Extract<TUserRoleInstitutionSettingsCamel, { role: R }>;

// END user.user_institution_roles TYPES

export interface IGetUsersResponse {
  data: IUserData;
  success: boolean;
  meta: object;
  error: object;
}

export interface IUserMeResponse {
  data: IGetUsersResponse;
}

export interface ISetLanguageRequest {
  language: string;
}

export interface IUploadProfilePictureResponse {
  data: { success: boolean; data: string };
}

export interface IMedicalSpecialty {
  id: string;
  infermedica_id: string;
  labels: Record<string, string>;
}

export interface IMedicalSpecialtyResponse {
  data: IMedicalSpecialty[];
}

export interface IInviteExternalUser {
  first_name: string;
  last_name: string;
  email?: string;
  phone?: IPhone;
}

export interface IInviteExternalUserRequest extends IInviteExternalUser {
  appointment_id: string;
}

export type TUniversalFormValues = Pick<
  TUserDataCamel,
  | 'firstName'
  | 'lastName'
  | 'gender'
  | 'phone'
  | 'timezone'
  | 'languagesSpoken'
  | 'medicalSpecialties'
  | 'licenseLocations'
  | 'isAutotimezone'
> & {
  deviceId: string;
  country: string;
  state: string;
  city: string;
  zip: string;
  address1: string;
  address2: string;
  dob: Date;
  insurance: string;
  insurancePolicyNumber: string;
  taxId: string;
  fax?: TNullable<IPhone>;
  height?: string;
  weight?: string;
};

export interface IUpdateUserByRoleAndInstitutionRequest {
  user_id: string;
  institution_id?: string;
  role?: EUserRoles;
}

export type TUpdateUserByRoleAndInstitutionRequest =
  TConvertKeys<IUpdateUserByRoleAndInstitutionRequest>;

export interface IUpdateDosespotRequest {
  dob: string;
  npi: string;
  institution_id: string;
}
export type TUpdateDosespotRequestCamel = TConvertKeys<IUpdateDosespotRequest>;

export interface IPostDosespotSsoRequest {
  user_institution_role_id: string;
}

export type TPostDosespotSsoRequestCamel =
  TConvertKeys<IPostDosespotSsoRequest>;
export interface IPostDosespotSsoResponse {
  url: string;
}

export type TPostDosespotSsoResponseCamel =
  TConvertKeys<IPostDosespotSsoResponse>;

export type TAccountSettingsParagraph = {
  content?: ReactNode;
  title?: string;
  description?: string;
  action?: ReactNode;
};
